/**
 * ModelPreviewService — experimental canvas-snapshot previews for Discover.
 *
 * For models without an $icon, renders the model through a hidden, off-screen
 * BCanvas (the app's own per-model recreation pattern) and captures a static
 * image via BCanvas.getCanvasImageDataUrl(). Results are cached by id+version.
 *
 * Renders are serialized because BCanvas is a singleton; model JSON fetches run
 * concurrently and are prefetched while a prior render is in flight.
 *
 * Known experiment-level leaks (identical to the build page's per-model canvas
 * recreation): each render spawns a routing Worker and a `visibilitychange`
 * listener via the BCanvas constructor. Caching bounds the render count.
 */
// NOTE: the canvas/Konva engine (`@/canvas`, ~185 kB gzip) is imported
// *dynamically* inside renderModelToDataUrl() so it stays out of the eager app
// bundle — Discover is otherwise its only eager consumer. The type-only import
// below is erased at build time and carries no runtime dependency.
import type { BCanvas } from '@/canvas';
import { getModel } from '@/api/models';
import type { Model } from '@/types';

const HIDDEN_W = 1600;
const HIDDEN_H = 1000;
/** Wait for importModel's async tail + image loads before capturing. */
const SETTLE_MS = 450;
/** Hard cap so a stuck render can't block the pool forever. */
const RENDER_TIMEOUT_MS = 9000;
/** Max canvases rendering at once. Each owns its figures (post instance-refactor)
 *  so they don't cross-talk; the cap just bounds memory / CPU. */
const MAX_CONCURRENT = 4;

const cache = new Map<string, string | null>();
const inflight = new Map<string, Promise<string | null>>();
/**
 * Bumped by disposePreviewRenderer() (e.g. on navigation away from Discover).
 * A render only constructs a BCanvas if its captured generation still matches —
 * this keeps a late preview from reassigning the global BCanvas.instance (used
 * by Build's fallback UI code) after the Build page has taken over.
 */
let generation = 0;
/** Unique element ids per render. */
let renderSeq = 0;

// Simple concurrency pool gate.
let activeRenders = 0;
const renderWaiters: Array<() => void> = [];
function acquireSlot(): Promise<void> {
  if (activeRenders < MAX_CONCURRENT) {
    activeRenders += 1;
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => renderWaiters.push(resolve)).then(() => {
    activeRenders += 1;
  });
}
function releaseSlot(): void {
  activeRenders -= 1;
  const next = renderWaiters.shift();
  if (next) next();
}

const noopGrowl = {
  success: () => {}, warning: () => {}, error: () => {}, info: () => {},
};
const noopGuiLog = {
  debug: () => {}, info: () => {}, warn: () => {}, error: () => {},
};

function cacheKey(id: string, version?: string | number): string {
  return `${id}:${version ?? ''}`;
}

/** jQuery-like scroll shim the canvas expects from setScrollArea(). */
function makeScrollShim(container: HTMLElement) {
  return Object.assign([container], {
    scrollLeft: (val?: number) => {
      if (val !== undefined) container.scrollLeft = val;
      return container.scrollLeft;
    },
    scrollTop: (val?: number) => {
      if (val !== undefined) container.scrollTop = val;
      return container.scrollTop;
    },
    scrollTo: (x: number, y: number) => container.scrollTo(x, y),
    getWidth: () => container.clientWidth || HIDDEN_W,
    getHeight: () => container.clientHeight || HIDDEN_H,
    getBoundingClientRect: () => container.getBoundingClientRect(),
  });
}

/** True when the model has at least one renderable node. */
function hasRenderableContent(model: Model): boolean {
  const m = model as unknown as Record<string, unknown>;
  const comps = m.components;
  let compCount = 0;
  if (Array.isArray(comps)) compCount = comps.length;
  else if (comps && typeof comps === 'object') compCount = Object.keys(comps).length;
  const len = (v: unknown) => (Array.isArray(v) ? v.length : 0);
  return (
    compCount > 0 ||
    len(m.functionals) > 0 ||
    len(m.iterators) > 0 ||
    len(m.inputs) > 0 ||
    len(m.outputs) > 0 ||
    len(m.annotations) > 0
  );
}

/** Wrap the callback-based capture in a promise with a timeout. */
function capture(bcanvas: BCanvas): Promise<string | null> {
  return new Promise((resolve) => {
    let settled = false;
    const done = (v: string | null) => {
      if (settled) return;
      settled = true;
      resolve(v);
    };
    const timer = setTimeout(() => done(null), RENDER_TIMEOUT_MS);
    try {
      (bcanvas as unknown as {
        getCanvasImageDataUrl: (cb: (url: string) => void) => void;
      }).getCanvasImageDataUrl((url: string) => {
        clearTimeout(timer);
        done(typeof url === 'string' && url.startsWith('data:') ? url : null);
      });
    } catch {
      clearTimeout(timer);
      done(null);
    }
  });
}

/** Build a hidden canvas, import the model, capture, then fully tear down.
 *  `gen` is the generation captured when the job was queued; if it no longer
 *  matches (Discover unmounted / Build took over), we skip entirely so a late
 *  render can't reassign the global BCanvas.instance out from under Build. */
async function renderModelToDataUrl(model: Model, gen: number): Promise<string | null> {
  if (!hasRenderableContent(model)) return null;
  // Last gate before we construct a canvas: bail if we're stale.
  if (gen !== generation) return null;

  // Lazy-load the canvas/Konva engine only when we actually render a preview —
  // keeps konva + the src/canvas tree + flatten-js out of the eager bundle. The
  // dynamic import is a new async gap, so re-check the generation afterwards in
  // case Discover unmounted (disposePreviewRenderer) while the chunk loaded.
  // The annotation sanitizer (DOMPurify) is lazy for the same reason; it loads
  // alongside. Keep `await import('@/canvas')` destructured directly — that is
  // the form the bundler tree-shakes; wrapping it in Promise.all keeps every
  // export of the canvas index.
  const sanitizerLoad = import('@/utils/sanitizeAnnotationHtml');
  const { canvas: createCanvas } = await import('@/canvas');
  const { sanitizeAnnotationHtml } = await sanitizerLoad;
  if (gen !== generation) return null;

  // Fully unique ids for BOTH host and stage. The BCanvas constructor now
  // resolves its scroll container as the stage container's parent (per-instance),
  // so unique ids no longer collide with the Build page or with each other —
  // which is what makes parallel rendering safe.
  const uid = ++renderSeq;
  const stageId = `brew-preview-stage-${uid}`;
  const host = document.createElement('div');
  host.id = `brew-preview-scroll-${uid}`;
  host.dataset.previewHost = 'true';
  host.style.cssText =
    `position:absolute;left:-99999px;top:0;width:${HIDDEN_W}px;height:${HIDDEN_H}px;overflow:hidden;pointer-events:none;`;
  const stageEl = document.createElement('div');
  stageEl.id = stageId;
  stageEl.style.cssText = 'width:100%;height:100%;';
  host.appendChild(stageEl);
  document.body.appendChild(host);

  let bcanvas: BCanvas | null = null;
  try {
    bcanvas = createCanvas(stageId, HIDDEN_W, HIDDEN_H);
    // Before importModel: annotations render as text without it (#2177).
    bcanvas.setAnnotationSanitizer(sanitizeAnnotationHtml);
    (bcanvas as unknown as { setScrollArea: (s: unknown) => void })
      .setScrollArea(makeScrollShim(host));
    (bcanvas as unknown as { setGrowlImp: (g: unknown) => void })
      .setGrowlImp(noopGrowl);
    (bcanvas as unknown as { setGUILOGImp: (g: unknown) => void })
      .setGUILOGImp(noopGuiLog);
    // Off-screen render: never pop the retired-functionals notice modal.
    (bcanvas as unknown as { setSuppressRetiredNotice: (s: boolean) => void })
      .setSuppressRetiredNotice(true);

    (bcanvas as unknown as { importModel: (m: Model) => void }).importModel(model);

    await new Promise((r) => setTimeout(r, SETTLE_MS));
    return await capture(bcanvas);
  } catch {
    return null;
  } finally {
    try {
      // stops the routing Worker once no other canvas is open (#2401)
      bcanvas?.destroy();
    } catch { /* best-effort */ }
    try {
      (bcanvas as unknown as { _stage?: { destroy: () => void } })?._stage?.destroy();
    } catch { /* best-effort */ }
    host.remove();
  }
}

/**
 * Request a snapshot data-URL for a model. Returns null when no snapshot is
 * available (no content, fetch/render error, or timeout) — callers keep their
 * placeholder. Fetches run concurrently; renders run concurrently too, bounded
 * by MAX_CONCURRENT (each canvas owns its figures, so they don't cross-talk).
 */
export function requestPreview(
  id: string,
  version?: string | number,
): Promise<string | null> {
  const key = cacheKey(id, version);
  if (cache.has(key)) return Promise.resolve(cache.get(key) ?? null);
  const existing = inflight.get(key);
  if (existing) return existing;

  // Snapshot the generation at request time; if dispose() bumps it before this
  // job renders, the job skips the canvas entirely and is not cached (so it can
  // be retried on a later Discover mount).
  const gen = generation;

  const job = (async () => {
    try {
      let model: Model | null = null;
      try {
        model = await getModel(id, { via: 'preview' });
      } catch {
        model = null;
      }
      if (!model || gen !== generation) return null;

      await acquireSlot();
      let result: string | null = null;
      try {
        result = gen === generation ? await renderModelToDataUrl(model, gen) : null;
      } finally {
        releaseSlot();
      }
      // Only cache results produced in the current generation.
      if (gen === generation) cache.set(key, result);
      return result;
    } finally {
      inflight.delete(key);
    }
  })();

  inflight.set(key, job);
  return job;
}

/**
 * Stop the preview renderer (call on Discover unmount). Bumps the generation so
 * any queued render skips the BCanvas singleton — critical so navigating to the
 * Build page mid-render can't corrupt Build's canvas. Also clears caches and
 * removes orphaned off-screen hosts.
 */
export function disposePreviewRenderer(): void {
  generation += 1;
  cache.clear();
  inflight.clear();
  // Remove any orphaned off-screen host left by an interrupted render.
  document.querySelectorAll('[data-preview-host]').forEach((el) => el.remove());
}
